import React, { useEffect, useState } from "react";

import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuAlertCircle,
  LuEye,
  LuEyeOff,
  LuLock,
  LuPhone,
} from "react-icons/lu";

import AuthLayout from "../../components/layout/AuthLayout";
import { useAppDispatch } from "../../app/hooks";
import { clearListStates } from "../../features/listState/listStateSlice";
import { POST } from "../../services/axios.service";
import { useAuth } from "../../context/AuthContext";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";

interface IFormInput {
  phone: string;
  password: string;
}

const ADMIN_CONTACT_HINT = "કૃપા કરીને તમારા એડમિનનો સંપર્ક કરો";

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, setIsAuthenticated } = useAuth();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IFormInput>({ mode: "onSubmit" });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);

    try {
      await POST("/v1/admin/login", {
        phone: data.phone,
        password: data.password,
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            await localStorage.setItem("authToken", response.data.data.token);
            sessionStorage.setItem("pinUnlocked", "1");
            // A new login starts every list afresh: first page, no filters.
            dispatch(clearListStates());
            await setIsAuthenticated(true);

            navigate(route.dashboard);
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, { ...(toastStyles as any) });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) navigate(route.dashboard);
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const message = sessionStorage.getItem("authMessage");

    if (message) {
      sessionStorage.removeItem("authMessage");
      setTimeout(() => toast.error(message, { ...(toastStyles as any) }), 0);
    }
  }, []);

  return (
    <AuthLayout>
      <div className="rounded-lg text-[hsl(222_84%_5%)] shadow-2xl border-0 bg-white/80 backdrop-blur-sm w-full max-w-md mx-auto">
        <div className="flex flex-col p-6 space-y-2 pb-8 pt-8 max-sm:px-5 max-[360px]:px-4 max-sm:pt-6 max-sm:pb-5">
          <div className="tracking-tight text-3xl font-bold text-center text-[#113F67] max-sm:text-2xl">
            લોગિન કરો
          </div>
          <div className="text-center text-gray-600 text-base max-sm:text-sm">
            તમારા ખાતામાં પ્રવેશ કરવા માટે તમારી વિગતો દાખલ કરો
          </div>
        </div>

        <div className="p-6 pt-0 space-y-6 px-8 pb-8 max-sm:px-5 max-[360px]:px-4 max-sm:pb-6">
          <form
            onSubmit={handleSubmit(handleOnSubmit)}
            className="space-y-6 max-sm:space-y-4"
          >
            <div className="space-y-3 max-sm:space-y-2">
              <label
                htmlFor="phone"
                className="text-sm font-semibold text-[#113F67]"
              >
                મોબાઇલ નંબર
              </label>

              <div className="relative">
                <LuPhone className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="phone"
                  type="tel"
                  maxLength={10}
                  placeholder="તમારો મોબાઇલ નંબર દાખલ કરો"
                  {...register("phone", {
                    required: "મોબાઇલ નંબર આવશ્યક છે",
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: "માન્ય 10 અંકનો મોબાઇલ નંબર દાખલ કરો",
                    },
                  })}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 h-14 text-base border-2 rounded-xl transition-all duration-200 ${
                    errors.phone
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
                {errors.phone && (
                  <LuAlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.phone && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.phone.message}
                </p>
              )}
            </div>

            <div className="space-y-3 max-sm:space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-semibold text-[#113F67]"
              >
                પાસવર્ડ
              </label>

              <div className="relative">
                <LuLock className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="તમારો પાસવર્ડ દાખલ કરો"
                  {...register("password", {
                    required: "પાસવર્ડ આવશ્યક છે",
                    minLength: {
                      value: 6,
                      message:
                        "પાસવર્ડ ઓછામાં ઓછા 6 અક્ષરનો હોવો જોઈએ",
                    },
                    maxLength: {
                      value: 50,
                      message:
                        "પાસવર્ડ 50 અક્ષરથી વધુ લાંબો ન હોવો જોઈએ",
                    },
                  })}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-12 pr-12 h-14 text-base border-2 rounded-xl transition-all duration-200 ${
                    errors.password
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1 h-11 w-11 flex items-center justify-center text-[#34699A] hover:text-[#113F67] transition-colors"
                >
                  {showPassword ? (
                    <LuEyeOff className="h-5 w-5" />
                  ) : (
                    <LuEye className="h-5 w-5" />
                  )}
                </button>
                {errors.password && (
                  <LuAlertCircle className="absolute right-12 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.password && (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="text-right">
              <div className="relative inline-block group">
                <button
                  type="button"
                  onClick={() =>
                    toast.info(ADMIN_CONTACT_HINT, {
                      ...(toastStyles as any),
                    })
                  }
                  className="text-sm text-[#34699A] hover:text-[#113F67] transition-colors hover:underline font-medium max-sm:py-3"
                >
                  પાસવર્ડ ભૂલી ગયા?
                </button>
                <span className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-gray-800 text-white px-3 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none transition-opacity">
                  {ADMIN_CONTACT_HINT}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none px-4 py-2 w-full h-14 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-lg rounded-xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  રાહ જુઓ...
                </div>
              ) : (
                "લોગિન કરો"
              )}
            </button>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};
export default LoginPage;
